import { useCallback, useState } from "react";
import { ApiError } from "@/lib/authenticatedFetch";

/**
 * Hook that provides plan limit error detection and modal state.
 * Detects errors with `{"type": "plan_limit"}` in the response details array,
 * so the frontend doesn't need to hardcode backend error codes.
 *
 * Usage:
 * ```ts
 * const { isPlanLimitError, showPlanLimitModal, setShowPlanLimitModal, showUpgradeModal, setShowUpgradeModal } = usePlanLimitGuard();
 *
 * try {
 *   await publishWorkflow(id);
 * } catch (error) {
 *   if (isPlanLimitError(error)) return; // modal is shown automatically
 *   toast.error(error.message);
 * }
 * ```
 */
export function usePlanLimitGuard() {
  const [showPlanLimitModal, setShowPlanLimitModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const isPlanLimitError = useCallback((error: unknown): boolean => {
    if (error instanceof ApiError && error.hasDetailType("plan_limit")) {
      setShowPlanLimitModal(true);
      return true;
    }
    return false;
  }, []);

  return {
    isPlanLimitError,
    showPlanLimitModal,
    setShowPlanLimitModal,
    showUpgradeModal,
    setShowUpgradeModal,
  };
}
